/**
 * Session aero parameters: defaults, limits, validation and Firestore mapping.
 *
 * Single source of truth shared by the params editor and the deep-analysis view.
 * Pure module — no Vue, no Firebase — so it can be unit tested in isolation.
 *
 * Two conventions matter here:
 *  - `eta` is held as a FRACTION (0.97) because the client-side CdA math in
 *    SessionDeepAnalysisView consumes it that way. It is only ever shown to the
 *    user as a percentage.
 *  - Firestore keys are asymmetric: totalMass/crr/eta are unprefixed while
 *    sessionAirBias/sessionAirFactor carry a `session` prefix.
 */

/**
 * Is this an aero-sensor session, as opposed to an imported activity file?
 *
 * Two different things live in the `sessions` collection:
 *  - Aero sessions, captured from the LeGizmo BLE sensor. These carry the
 *    aero parameters (total mass, crr, eta, air bias, air factor) and can be
 *    processed for CdA. iOS does not write a `source` field, so these are
 *    identified by the absence of an import source.
 *  - Imported activity files (currently Garmin FIT), which are a basic ride
 *    view only. They have no rider mass and no aero parameters, so those
 *    metrics must not be displayed for them.
 *
 * The eventual goal is to merge a Garmin FIT with its corresponding aero
 * session to enrich it; until then the two are shown differently.
 */
export function isAeroSession(session: Record<string, any> | null | undefined): boolean {
  const source = session?.source
  if (!source) return true // iOS aero sessions write no source field
  return String(source).toLowerCase() === 'gizmo'
}

/**
 * Is this an imported FIT-file session for a cycling activity?
 *
 * `sport` is written verbatim from the FIT file's own sport enum by the
 * import pipeline (functions/src/garmin.ts's ingestFitBuffer) -- e.g.
 * 'cycling', 'running'. Used to gate cycling-only features (like Virtual
 * Elevation analysis) on GarminActivityDetail.vue.
 */
export function isCyclingActivity(session: Record<string, any> | null | undefined): boolean {
  return String(session?.sport ?? '').toLowerCase() === 'cycling'
}

export interface SessionParamsForm {
  /** kg, rider + bike + equipment -> totalMass */
  mass: number
  /** dimensionless -> crr */
  crr: number
  /** FRACTION, e.g. 0.97 -> eta. Displayed as a percentage. */
  eta: number
  /** -> sessionAirBias */
  air_bias: number
  /** -> sessionAirFactor */
  air_factor: number
  /** m², optional -> targetCdA */
  targetCdA: number | null
  /** -> name */
  name: string
  /** -> notes */
  notes: string
  /** -> usingOutAndBackPattern */
  usingOutAndBackPattern: boolean
  /** -> adjustCrrDueToHeat. Applied by iOS only; the web pipeline ignores it. */
  adjustCrrDueToHeat: boolean
}

/**
 * Deliberately aligned to the backend fallbacks in aero.py, not to the iOS
 * Basic defaults (95.0 / 0.005 / 1.0 / 2.0). What the form shows for an
 * unset field is then what the pipeline would actually use.
 */
export const SESSION_PARAM_DEFAULTS: SessionParamsForm = {
  mass: 80.0,
  crr: 0.004,
  eta: 0.97,
  air_bias: 0.5,
  air_factor: 1.0,
  targetCdA: null,
  name: '',
  notes: '',
  usingOutAndBackPattern: false,
  adjustCrrDueToHeat: false
}

export interface SessionParamLimit {
  min: number
  max: number
  decimals: number
  label: string
  hint: string
}

/**
 * Ranges mirror the iOS Basic editor (BasicSessionSettingsView), which is
 * stricter than Pro — Pro allows 40–200 kg and does not validate CRR at all.
 *
 * `eta` is bounded as a fraction so validation runs in the same units as the
 * physics; only the error message and the input render as a percentage.
 */
export const SESSION_PARAM_LIMITS: Record<
  'mass' | 'crr' | 'eta' | 'air_bias' | 'air_factor' | 'targetCdA',
  SessionParamLimit
> = {
  mass: {
    min: 40,
    max: 250,
    decimals: 1,
    label: 'System Weight (kg)',
    hint: 'Rider + bike + equipment'
  },
  crr: {
    min: 0.0001,
    max: 0.05,
    decimals: 4,
    label: 'Rolling Resistance (CRR)',
    hint: 'Typical road CRR: 0.0030 – 0.0060'
  },
  eta: {
    min: 0.8,
    max: 1.0,
    decimals: 4,
    label: 'Drivetrain Efficiency (ETA %)',
    hint: '80 – 100'
  },
  air_bias: {
    min: -6.0,
    max: 6.0,
    decimals: 2,
    label: 'Air Pressure Bias',
    hint: '-6.0 to +6.0'
  },
  air_factor: {
    min: 0.11,
    max: 2.11,
    decimals: 3,
    label: 'Air Factor',
    hint: '0.11 – 2.11'
  },
  targetCdA: {
    min: 0.05,
    max: 0.9,
    decimals: 3,
    label: 'Target Reference CdA',
    hint: 'Optional, e.g. 0.200'
  }
}

export function etaFractionToPercent(fraction: number): number {
  if (!Number.isFinite(fraction)) return NaN
  // Round to 1 dp to avoid 0.97 -> 96.99999999999999 in the input.
  return Math.round(fraction * 1000) / 10
}

export function etaPercentToFraction(percent: number): number {
  if (!Number.isFinite(percent)) return NaN
  return Math.round(percent * 100) / 10000
}

/** Coerce a Firestore value that may be a number, a numeric string, or absent. */
function toNumber(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') return fallback
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : fallback
}

function toOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

/** Build an editor form from a raw Firestore session document. */
export function sessionDocToForm(doc: Record<string, any> | null | undefined): SessionParamsForm {
  const d = doc ?? {}
  return {
    mass: toNumber(d.totalMass, SESSION_PARAM_DEFAULTS.mass),
    crr: toNumber(d.crr, SESSION_PARAM_DEFAULTS.crr),
    eta: toNumber(d.eta, SESSION_PARAM_DEFAULTS.eta),
    air_bias: toNumber(d.sessionAirBias, SESSION_PARAM_DEFAULTS.air_bias),
    air_factor: toNumber(d.sessionAirFactor, SESSION_PARAM_DEFAULTS.air_factor),
    targetCdA: toOptionalNumber(d.targetCdA),
    name: typeof d.name === 'string' ? d.name : '',
    notes: typeof d.notes === 'string' ? d.notes : '',
    usingOutAndBackPattern: d.usingOutAndBackPattern === true,
    adjustCrrDueToHeat: d.adjustCrrDueToHeat === true
  }
}

/**
 * Map the form back onto Firestore keys. Only these keys are written, so the
 * caller can pass the result straight to updateDoc without clobbering results
 * fields such as bestCdA or analyzed_at.
 */
export function formToSessionDoc(form: SessionParamsForm): Record<string, any> {
  return {
    totalMass: form.mass,
    crr: form.crr,
    eta: form.eta,
    sessionAirBias: form.air_bias,
    sessionAirFactor: form.air_factor,
    targetCdA: form.targetCdA,
    name: form.name.trim(),
    notes: form.notes,
    usingOutAndBackPattern: form.usingOutAndBackPattern,
    adjustCrrDueToHeat: form.adjustCrrDueToHeat
  }
}

/**
 * Returns a list of human-readable problems; an empty array means valid.
 *
 * Mirrors iOS: reject and block the save rather than silently clamping, so the
 * user always knows what the stored value will be.
 */
export function validateSessionParams(form: SessionParamsForm): string[] {
  const errors: string[] = []

  const checkRange = (key: keyof typeof SESSION_PARAM_LIMITS, value: number, unit = '') => {
    const limit = SESSION_PARAM_LIMITS[key]
    if (!Number.isFinite(value)) {
      errors.push(`${limit.label} must be a number.`)
      return
    }
    if (value < limit.min || value > limit.max) {
      errors.push(`${limit.label} must be between ${limit.min}${unit} and ${limit.max}${unit}.`)
    }
  }

  if (!form.name || !form.name.trim()) {
    errors.push('Session Name cannot be empty.')
  }

  checkRange('mass', form.mass, ' kg')

  // Reported in percent because that is what the user typed.
  if (!Number.isFinite(form.eta)) {
    errors.push('Drivetrain Efficiency must be a number.')
  } else if (form.eta < SESSION_PARAM_LIMITS.eta.min || form.eta > SESSION_PARAM_LIMITS.eta.max) {
    errors.push('Drivetrain Efficiency must be between 80% and 100%.')
  }

  checkRange('crr', form.crr)
  checkRange('air_bias', form.air_bias)
  checkRange('air_factor', form.air_factor)

  // Optional: many existing sessions have no target, and blocking a mass edit
  // on a missing target would be hostile.
  if (form.targetCdA !== null) {
    checkRange('targetCdA', form.targetCdA)
  }

  return errors
}
